import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, CircleNotch, WarningCircle, LockKey, CheckCircle } from "@phosphor-icons/react";

function PinField({ label, value, onChange, testid }) {
  return (
    <>
      <label className="block mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground text-left">{label}</label>
      <input
        data-testid={testid} type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6}
        autoComplete="one-time-code" required value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
        className="mt-1.5 w-full border border-border bg-background px-4 py-3 text-center text-2xl tracking-[0.6em] rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
      />
    </>
  );
}

export default function PinReset() {
  const [state, setState] = useState(() =>
    new URLSearchParams(window.location.search).get("e") ? "error" : "form");
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setMessage("");
    if (pin.length !== 6) { setMessage("The Rotary Access PIN must be exactly 6 digits."); return; }
    if (pin !== confirm) { setMessage("The PINs do not match."); return; }
    setBusy(true);
    try {
      await api.post("/devices/pin/reset/complete", { pin });
      await refresh();
      setState("done");
    } catch (err) {
      const status = err?.response?.status;
      const d = err?.response?.data?.detail;
      if (status === 400) { setState("error"); return; }
      setMessage(typeof d === "string" ? d : "Reset was not completed. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="pin-reset-page">
      <div className="max-w-md w-full border border-border bg-surface rounded-md p-8 text-center">
        <div className="h-10 w-10 bg-primary rounded flex items-center justify-center mx-auto">
          <ShieldCheck size={22} weight="fill" className="text-white" />
        </div>

        {state === "error" && (
          <>
            <div className="mt-5 mx-auto text-amber-600"><WarningCircle size={32} weight="duotone" /></div>
            <h1 className="mt-3 t-section-title" data-testid="pin-reset-error-title">Reset link unavailable</h1>
            <p className="mt-2 t-support text-muted-foreground">
              This PIN reset link cannot be used. It may be invalid, expired or already used.
              Request a new one from the unlock screen on your paired phone.
            </p>
          </>
        )}

        {state === "done" && (
          <>
            <div className="mt-5 mx-auto text-primary"><CheckCircle size={32} weight="duotone" /></div>
            <h1 className="mt-3 t-section-title" data-testid="pin-reset-done-title">PIN updated</h1>
            <p className="mt-2 t-support text-muted-foreground">
              Your Rotary Access PIN has been changed and this device&apos;s secure session was refreshed.
            </p>
            <button data-testid="pin-reset-continue" onClick={() => navigate("/", { replace: true })}
              className="mt-5 inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 t-support font-semibold rounded-md hover:bg-primary/90">
              Continue
            </button>
          </>
        )}

        {state === "form" && (
          <form onSubmit={submit} data-testid="pin-reset-form">
            <LockKey size={40} weight="duotone" className="text-primary mx-auto mt-5" />
            <h1 className="mt-4 font-display font-extrabold tracking-tighter text-2xl">Reset your Rotary Access PIN</h1>
            <p className="mt-3 t-support text-muted-foreground text-left">
              This works only on your already-paired Rotary mobile device. It cannot authorise a
              new or different device.
            </p>
            <PinField label="New Rotary Access PIN" value={pin} onChange={setPin} testid="pin-reset-input" />
            <PinField label="Confirm PIN" value={confirm} onChange={setConfirm} testid="pin-reset-confirm-input" />
            {message && <p data-testid="pin-reset-error" className="mt-3 text-sm text-red-700">{message}</p>}
            <button
              data-testid="pin-reset-submit-button" type="submit" disabled={busy}
              className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 transition-colors"
            >
              {busy ? <CircleNotch size={18} className="animate-spin" weight="bold" /> : <LockKey size={18} weight="bold" />}
              Save new PIN
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
